// TrackOrderSteps.jsx
import { useEffect, useState } from "react";
import { ORDER_STEPS } from "../Utility";
import { OrderTrackStatus, ServiceType } from "../../enums/enums";
import { updateOrderPayment } from "../../store/order-slice";
import { useDispatch, useSelector } from "react-redux";

const TrackOrderSteps = ({ item }) => {
    // Filter steps based on service type
    const steps = (item.serviceType === ServiceType.DineIn || item.serviceType === ServiceType.TakeAway)
        ? ORDER_STEPS.filter(step =>
            step.value === OrderTrackStatus.Preparing ||
            step.value === OrderTrackStatus.Ready ||
            step.value === OrderTrackStatus.Completed
        )
        : ORDER_STEPS;

    // backend driven
    const [status, setStatus] = useState(item.orderStatus);
    const [currentIndex, setCurrentIndex] = useState(null);
    const userRole = useSelector((state) => state.auth.selectedRole);
    const dispatch = useDispatch();

    /* ---------- API CALL PLACEHOLDER ---------- */
    const onStatusChange = (value) => {

        setStatus(value);

        const updateOrderData = {
            _id: item._id,
            orderStatus: value,
        }


        dispatch(updateOrderPayment({
            ...userRole,
            data: updateOrderData
        }));
    };

    /* ---------- SYNC UI WITH STATUS ---------- */
    useEffect(() => {
        // No stepper states
        if (
            status === OrderTrackStatus.Pending ||
            status === OrderTrackStatus.Rejected ||
            status === OrderTrackStatus.Cancelled
        ) {
            setCurrentIndex(null);
            return;
        }

        // Accepted → start stepper
        if (status === OrderTrackStatus.Accepted) {
            setCurrentIndex(0); // Preparing
            return;
        }

        // Preparing & beyond
        const index = steps.findIndex(
            (step) => step.value === status
        );

        setCurrentIndex(index >= 0 ? index : null);
    }, [status, steps]);

    /* ---------- Derived Steps ---------- */
    const current =
        currentIndex !== null ? steps[currentIndex] : null;
    const next =
        currentIndex !== null ? steps[currentIndex + 1] : null;

    const moveNextHandler = () => {
        if (!next) return;
        onStatusChange(next.value);
    };

    /* ---------- Decision ---------- */
    const acceptOrderHandler = () => {
        onStatusChange(OrderTrackStatus.Accepted);
        window.location.reload();
    };

    const rejectOrderHandler = () => {
        onStatusChange(OrderTrackStatus.Rejected);
        window.location.reload();
    };

    /* ---------- UI ---------- */
    return (
        <div className="">

            {/* PENDING */}
            {status === OrderTrackStatus.Pending && (
                <div className="decision-actions d-flex gap-2">
                    <button
                        className="btn-decision btn-accept btn btn-success text-white py-2"
                        onClick={acceptOrderHandler}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="border border-white border-2 rounded-circle me-1">
                            <path d="M20 6L9 17l-5-5" />
                        </svg>
                        Accept
                    </button>
                    <button
                        className="btn-decision btn-reject btn btn-danger py-2"
                        onClick={rejectOrderHandler}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="border border-white border-2 rounded-circle me-1">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                        Reject
                    </button>
                </div>
            )}

            {/* REJECTED */}
            {status === OrderTrackStatus.Rejected && (
                <div className="status-badge status-rejected border border-2 border-danger d-inline-flex py-2 px-3 align-items-center gap-1">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-danger">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M15 9l-6 6m0-6l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span className="text-danger fw-medium">Order Rejected</span>
                </div>
            )}

            {/* CANCELLED */}
            {status === OrderTrackStatus.Cancelled && (
                <div className="status-badge status-cancelled">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M15 9l-6 6m0-6l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>Order Cancelled</span>
                </div>
            )}

            {/* STEPPER */}
            {current && (
                <div className="">
                    {status !== OrderTrackStatus.Completed && (
                        <div className="track-order-steps-container">
                            {/* <div className="stepper-container"> */}
                            <div className="stepper-progress">
                                <div className="stepper-steps">
                                    {steps.map((step, index) => {
                                        const isCompleted = index < currentIndex;
                                        const isCurrent = index === currentIndex;
                                        const isUpcoming = index > currentIndex;

                                        return (
                                            <div key={step.key} className="stepper-step-wrapper">
                                                <div className={`stepper-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isUpcoming ? 'upcoming' : ''}`}>
                                                    <div className="step-indicator">
                                                        {isCompleted ? (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                                <path d="M20 6L9 17l-5-5" />
                                                            </svg>
                                                        ) : (
                                                            <span className="step-number">{index + 1}</span>
                                                        )}
                                                    </div>
                                                    <span className="step-label">{step.key}</span>
                                                </div>
                                                {index < steps.length - 1 && (
                                                    <div className={`step-connector ${isCompleted ? 'completed' : ''}`}></div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* </div> */}
                        </div>
                    )}

                    <div className="stepper-action text-center">
                        {next ? (
                            <button
                                className="btn-next-step btn btn-success text-white py-3"
                                onClick={moveNextHandler}
                            >
                                <span>Mark as {next.key}</span>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="border border-white border-2 rounded-circle ms-2 ">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        ) : (
                            <div className="order-completed border border-2 border-success d-inline-flex py-2 px-3 align-items-center gap-1 bg-success text-white">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <path d="M22 4L12 14.01l-3-3" />
                                </svg>
                                <span>Order Completed 🎉</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrackOrderSteps;
